/**
 * A general class that allows you to declaratively set a class toggle on a button or other
 * clickable element.
 */
class ToggleClass {
  constructor() {
    this.body = document.body;
    this.toggleElements = [];
    this.boundOnClick = this.onClick.bind(this);
  }

  /**
   * Get all of the elements with the toggle class, and attach their event listeners.
   *
   * @return void
   */
  init() {
    this.toggleElements = document.querySelectorAll('.js-toggle-class');
    this.toggleElements.forEach(el => el.addEventListener('click', this.boundOnClick));
  }

  /**
   * Remove event listeners and reset the array of toggleElements.
   *
   * @return void
   */
  deinit() {
    this.toggleElements.forEach(el => el.removeEventListener('click', this.boundOnClick));
    this.toggleElements = [];
  }

  /**
   * Handle the click on the toggle trigger element.
   *
   * @param {Object} e Event data.
   *
   * @return void
   */
  onClick(e) {
    const src = e.target.closest('.js-toggle-class');
    const { tElemPos, tSelector, tClass, tTrigger, tToggleText } = src.dataset;

    if (tTrigger) {
      const evt = new Event(tTrigger);
      window.dispatchEvent(evt);
    }

    if (tToggleText) {
      const activeText = src.innerText;
      src.innerText = tToggleText;
      src.dataset.tToggleText = activeText;
    }

    // Since modifying classes on the body is common, check for that selector first.
    if (tSelector === 'body') {
      this.toggle(this.body, tClass);
      return;
    }

    // Depending on the position of the element, determine the element that should have its class
    // toggled and toggle it.
    switch (tElemPos) {
      case 'child':
        this.toggle(src.querySelectorAll(tSelector), tClass);
        break;
      case 'parent':
        this.toggle(src.closest(tSelector), tClass);
        break;
      default:
        this.toggle(src, tClass);
        break;
    }
  }

  /**
   * Toggle a class on an element or a NodeList of elements.
   *
   * @param {Element|NodeList} el Can be a singular element, or a NodeList of elements.
   * @param {String} tclass The class to apply to the elements.
   *
   * @return void
   */
  toggle(el, tclass) {
    // Since el can be a NodeList, check if it is and, if so, iterate to toggle the class on all
    // elements in the NodeList. Otherwise, el should just be a singular element so we can toggle
    // the class directly on it.
    if (el.length > 1) {
      el.forEach(childEl => childEl.classList.toggle(tclass));
      return;
    }

    el.classList.toggle(tclass);
  }
}

export default ToggleClass;
